/**
 * Vincent B785 (Eric 2026-02-18 SPEC STRICK PROD) — Modal Diagnostic Whisper.
 *
 * Appelle `GET /api/videos/{id}/whisper-diagnostic` et affiche joliment
 * les métriques : durée vidéo, taille MP3, count/max_end final vs raw,
 * dedup_stats, verdict automatique.
 *
 * Cible : Vincent audite une vidéo qui semble avoir une transcription
 * tronquée SANS avoir à ouvrir la DevTools ni à curler l'API.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const fmtDuration = (s) => {
  if (s === null || s === undefined) return "?";
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}m ${String(r).padStart(2, "0")}s`;
};

const VERDICT_MESSAGES = {
  fr: {
    audio_mp3_truncated: "Le MP3 extrait est plus petit que ce qui est attendu pour la durée de la vidéo. Piste : l'audio source est peut-être corrompu.",
    segments_stop_before_video_end: "Les segments s'arrêtent avant la fin de la vidéo. Piste : Whisper a peut-être stoppé sur un problème de format audio.",
    regroup_dropped_late_segments: "Le regroup diarisation a fait disparaître des segments tardifs. Piste : bug de fusion, à investiguer.",
    aggressive_dedup: "Le filtre anti-hallucination a retiré > 50 % des segments. Piste : audio de coaching / voix douce. Utilise « Re-transcrire sans dedup ».",
  },
  en: {
    audio_mp3_truncated: "Extracted MP3 is smaller than expected for the video duration. Hint: source audio may be corrupt.",
    segments_stop_before_video_end: "Segments end before the video does. Hint: Whisper may have stopped on an audio format issue.",
    regroup_dropped_late_segments: "Diarization regroup dropped late segments. Hint: fusion bug to investigate.",
    aggressive_dedup: "The anti-hallucination filter removed > 50% of segments. Hint: coaching audio / quiet voice. Use « Re-transcribe without dedup ».",
  },
};

export default function WhisperDiagnosticModal({ open, videoId, api, lang = "fr", onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !videoId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(
          `${api}/api/videos/${videoId}/whisper-diagnostic`,
          { withCredentials: true, timeout: 30000 }
        );
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message);
          toast.error(
            lang === "fr"
              ? `Erreur diagnostic : ${err.response?.data?.detail || err.message}`
              : `Diagnostic error: ${err.response?.data?.detail || err.message}`
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, videoId, api, lang]);

  if (!open) return null;

  const ds = data?.transcript?.dedup_stats;
  const dedupRatio = ds && ds.before_dedup > 0 ? ds.removed / ds.before_dedup : 0;
  const verdictKey = data?.verdict?.[0];
  const verdictMsg = verdictKey ? VERDICT_MESSAGES[lang]?.[verdictKey] || verdictKey : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="whisper-diagnostic-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-zinc-200 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
            <span aria-hidden="true">🩺</span>
            {lang === "fr" ? "Diagnostic Whisper" : "Whisper diagnostic"}
          </h2>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 text-xl leading-none"
            data-testid="whisper-diagnostic-close-btn"
            aria-label="close"
          >×</button>
        </div>

        <div className="p-4 space-y-4">
          {loading ? (
            <p className="text-sm text-zinc-500 italic">
              {lang === "fr" ? "Analyse en cours…" : "Analyzing…"}
            </p>
          ) : error ? (
            <p className="text-sm text-rose-700" data-testid="whisper-diagnostic-error">
              {error}
            </p>
          ) : data ? (
            <>
              {/* Verdict — headline si présent */}
              {verdictKey ? (
                <div
                  className="p-3 border border-amber-400 bg-amber-50 rounded"
                  data-testid="whisper-diagnostic-verdict"
                >
                  <p className="font-bold text-amber-900 mb-1 flex items-center gap-1.5">
                    <span aria-hidden="true">⚠️</span>
                    {lang === "fr" ? "Verdict automatique : " : "Automatic verdict: "}
                    <code className="font-mono">{verdictKey}</code>
                  </p>
                  <p className="text-[12px] text-amber-800 leading-snug">{verdictMsg}</p>
                </div>
              ) : (
                <div
                  className="p-3 border border-emerald-300 bg-emerald-50 rounded"
                  data-testid="whisper-diagnostic-verdict-clean"
                >
                  <p className="font-bold text-emerald-800 flex items-center gap-1.5">
                    <span aria-hidden="true">✅</span>
                    {lang === "fr" ? "Aucune anomalie détectée" : "No anomaly detected"}
                  </p>
                </div>
              )}

              {/* Vidéo */}
              <section data-testid="whisper-diagnostic-video">
                <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
                  {lang === "fr" ? "Vidéo" : "Video"}
                </h3>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                  <dt className="text-zinc-500">{lang === "fr" ? "Titre" : "Title"}</dt>
                  <dd className="text-zinc-900 truncate" title={data.video?.title}>{data.video?.title || "?"}</dd>
                  <dt className="text-zinc-500">{lang === "fr" ? "Durée" : "Duration"}</dt>
                  <dd className="text-zinc-900 tabular-nums">{data.video?.duration_human || fmtDuration(data.video?.duration_seconds)}</dd>
                  <dt className="text-zinc-500">Pipeline</dt>
                  <dd className="text-zinc-900 font-mono text-[11px]">
                    {data.video?.pipeline_status || "?"} / {data.video?.pipeline_stage || "?"}
                  </dd>
                  {data.video?.error && (
                    <>
                      <dt className="text-zinc-500">{lang === "fr" ? "Erreur" : "Error"}</dt>
                      <dd className="text-rose-700 text-[11px]">{data.video.error}</dd>
                    </>
                  )}
                </dl>
              </section>

              {/* Audio MP3 */}
              <section data-testid="whisper-diagnostic-audio">
                <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
                  {lang === "fr" ? "Audio extrait" : "Extracted audio"}
                </h3>
                {data.audio_asset?.present ? (
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                    <dt className="text-zinc-500">{lang === "fr" ? "Taille" : "Size"}</dt>
                    <dd className="text-zinc-900 tabular-nums">
                      {data.audio_asset.size_mb ? `${data.audio_asset.size_mb} MB` : "?"}
                    </dd>
                    <dt className="text-zinc-500">Storage</dt>
                    <dd className="text-zinc-500 text-[10px] font-mono truncate" title={data.audio_asset.storage_path}>
                      {data.audio_asset.storage_path || "?"}
                    </dd>
                  </dl>
                ) : (
                  <p className="text-[11px] text-rose-700 italic">
                    {lang === "fr" ? "Aucun MP3 extrait — le pipeline n'a pas encore extrait l'audio." : "No MP3 extracted yet."}
                  </p>
                )}
              </section>

              {/* Segments */}
              <section data-testid="whisper-diagnostic-segments">
                <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
                  {lang === "fr" ? "Segments" : "Segments"}
                </h3>
                <div className="grid grid-cols-2 gap-3 text-[12px]">
                  <div className="p-2 border border-zinc-200 rounded">
                    <p className="text-[10px] uppercase text-zinc-500 mb-1">
                      Raw (Whisper post-dedup)
                    </p>
                    <p className="text-2xl font-bold tabular-nums text-zinc-900" data-testid="whisper-diagnostic-raw-count">
                      {data.segments?.raw_count ?? 0}
                    </p>
                    <p className="text-[10px] text-zinc-500 tabular-nums">
                      → {data.segments?.raw_max_end_human || "?"}
                    </p>
                  </div>
                  <div className="p-2 border border-zinc-200 rounded">
                    <p className="text-[10px] uppercase text-zinc-500 mb-1">
                      Final ({lang === "fr" ? "post-diarisation" : "post-diar"})
                    </p>
                    <p className="text-2xl font-bold tabular-nums text-zinc-900" data-testid="whisper-diagnostic-final-count">
                      {data.segments?.final_count ?? 0}
                    </p>
                    <p className="text-[10px] text-zinc-500 tabular-nums">
                      → {data.segments?.final_max_end_human || "?"}
                    </p>
                  </div>
                </div>
              </section>

              {/* Vincent B790 (Eric 2026-02-18 SPEC STRICK PROD) —
                  Trace UI : samples des 5 premiers/derniers segments
                  raw pour repérer immédiatement les hallucinations
                  (`« Oui / Oui / Oui »`) sans DevTools. */}
              {(data.segments?.raw_head_samples?.length > 0 || data.segments?.raw_tail_samples?.length > 0) && (
                <section data-testid="whisper-diagnostic-samples">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
                    {lang === "fr" ? "Aperçu des segments (raw)" : "Segment preview (raw)"}
                    <span className="ml-2 text-[9px] font-normal normal-case text-zinc-400">
                      {lang === "fr" ? "utile pour repérer les hallucinations" : "handy to spot hallucinations"}
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 gap-2">
                    {data.segments?.raw_head_samples?.length > 0 && (
                      <div className="border border-zinc-200 rounded overflow-hidden" data-testid="whisper-diagnostic-head-samples">
                        <p className="px-2 py-1 text-[10px] uppercase text-zinc-500 bg-zinc-50 border-b border-zinc-200 font-bold">
                          {lang === "fr" ? "5 premiers segments" : "First 5 segments"}
                        </p>
                        <ul className="divide-y divide-zinc-100">
                          {data.segments.raw_head_samples.map((s, i) => {
                            const start = Number(s.start || 0);
                            const end = Number(s.end || 0);
                            const text = (s.text || "").trim();
                            const isShort = text.length < 15;
                            return (
                              <li
                                key={`h-${i}`}
                                className={`flex gap-2 px-2 py-1 text-[11px] ${isShort ? "bg-amber-50/40" : ""}`}
                                data-testid={`whisper-diagnostic-head-${i}`}
                              >
                                <span className="text-zinc-400 tabular-nums flex-shrink-0 w-24">
                                  {Math.floor(start / 60)}:{String(Math.round(start % 60)).padStart(2, "0")} → {Math.floor(end / 60)}:{String(Math.round(end % 60)).padStart(2, "0")}
                                </span>
                                <span className={`flex-1 ${isShort ? "italic text-amber-800" : "text-zinc-800"}`}>
                                  {text || <span className="text-rose-500">(vide)</span>}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    {data.segments?.raw_tail_samples?.length > 0 && (
                      <div className="border border-zinc-200 rounded overflow-hidden" data-testid="whisper-diagnostic-tail-samples">
                        <p className="px-2 py-1 text-[10px] uppercase text-zinc-500 bg-zinc-50 border-b border-zinc-200 font-bold">
                          {lang === "fr" ? "5 derniers segments" : "Last 5 segments"}
                        </p>
                        <ul className="divide-y divide-zinc-100">
                          {[...data.segments.raw_tail_samples].reverse().map((s, i) => {
                            const start = Number(s.start || 0);
                            const end = Number(s.end || 0);
                            const text = (s.text || "").trim();
                            const isShort = text.length < 15;
                            return (
                              <li
                                key={`t-${i}`}
                                className={`flex gap-2 px-2 py-1 text-[11px] ${isShort ? "bg-amber-50/40" : ""}`}
                                data-testid={`whisper-diagnostic-tail-${i}`}
                              >
                                <span className="text-zinc-400 tabular-nums flex-shrink-0 w-24">
                                  {Math.floor(start / 60)}:{String(Math.round(start % 60)).padStart(2, "0")} → {Math.floor(end / 60)}:{String(Math.round(end % 60)).padStart(2, "0")}
                                </span>
                                <span className={`flex-1 ${isShort ? "italic text-amber-800" : "text-zinc-800"}`}>
                                  {text || <span className="text-rose-500">(vide)</span>}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-zinc-500 italic mt-1">
                    {lang === "fr"
                      ? "ℹ️ Les segments < 15 caractères sont surlignés en ambre — pattern typique d'hallucination (« Oui », « Ok », « Voilà » répétés)."
                      : "ℹ️ Segments < 15 chars are highlighted amber — typical hallucination pattern (« Yes », « OK » repeated)."}
                  </p>
                </section>
              )}

              {/* Dedup stats */}
              {ds && ds.before_dedup > 0 && (
                <section data-testid="whisper-diagnostic-dedup">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
                    {lang === "fr" ? "Filtre anti-hallucination" : "Anti-hallucination filter"}
                    {ds.skipped && (
                      <span className="ml-2 px-1.5 py-0.5 bg-zinc-200 text-zinc-700 text-[9px] rounded font-normal normal-case">
                        SKIPPED
                      </span>
                    )}
                  </h3>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                    <dt className="text-zinc-500">{lang === "fr" ? "Retirés" : "Removed"}</dt>
                    <dd className="text-zinc-900 tabular-nums">
                      <span className="font-bold">{ds.removed}</span>
                      <span className="text-zinc-500"> / {ds.before_dedup}</span>
                      <span className={`ml-2 font-bold ${dedupRatio >= 0.75 ? "text-amber-900" : dedupRatio >= 0.5 ? "text-amber-700" : "text-zinc-600"}`}>
                        ({Math.round(dedupRatio * 100)} %)
                      </span>
                    </dd>
                    <dt className="text-zinc-500">Prompt echo</dt>
                    <dd className="text-zinc-900 tabular-nums">{ds.prompt_echo || 0}</dd>
                    <dt className="text-zinc-500">Repeats</dt>
                    <dd className="text-zinc-900 tabular-nums">{ds.repeats || 0}</dd>
                    <dt className="text-zinc-500">Intra collapsed</dt>
                    <dd className="text-zinc-900 tabular-nums">{ds.intra_collapsed || 0}</dd>
                    {ds.jaccard_threshold != null && (
                      <>
                        <dt className="text-zinc-500">Jaccard threshold</dt>
                        <dd className="text-zinc-900 tabular-nums font-mono">{ds.jaccard_threshold}</dd>
                      </>
                    )}
                    {/* Vincent B789 (Eric 2026-02-18) — Filtre no_speech_prob>0.6
                        appliqué AVANT le dedup. Compteur exposé pour audit
                        rétroactif (visible seulement si > 0).
                        Vincent B846 (Eric 2026-02-14 SPEC STRICK PROD) — Le
                        filtre est maintenant COMBINÉ (nsp + avg_logprob).
                        Affiche les seuils utilisés pour transparence. */}
                    {ds.hallucination_filtered_pre_dedup > 0 && (
                      <>
                        <dt className="text-zinc-500">
                          {lang === "fr" ? "Filtre silence (pré-dedup)" : "Silence filter (pre-dedup)"}
                        </dt>
                        <dd className="text-zinc-900 tabular-nums" data-testid="whisper-diagnostic-hallu-pre">
                          <span className="font-bold text-amber-800">{ds.hallucination_filtered_pre_dedup}</span>
                          {ds.hallucination_filter_thresholds ? (
                            <span className="text-zinc-400 text-[10px] ml-1">
                              (no_speech &gt; {ds.hallucination_filter_thresholds.no_speech_min}
                              {" "}ET avg_logprob &lt; {ds.hallucination_filter_thresholds.avg_logprob_max})
                            </span>
                          ) : (
                            <span className="text-zinc-400 text-[10px] ml-1">
                              {lang === "fr" ? "(no_speech_prob > 0.6)" : "(no_speech_prob > 0.6)"}
                            </span>
                          )}
                        </dd>
                      </>
                    )}
                  </dl>
                  {/* Vincent B846 (Eric 2026-02-14 SPEC STRICK PROD) — Détail
                      des segments filtrés (max 20). Sans ça, impossible de
                      savoir QUELS segments ont été jetés → Vincent voit
                      « il manque les 45 premières secondes » et ne peut pas
                      auditer sans replonger dans les logs Kubernetes. */}
                  {Array.isArray(ds.hallucination_filter_samples) && ds.hallucination_filter_samples.length > 0 && (
                    <div className="mt-3 border border-amber-200 rounded-lg bg-amber-50/50 overflow-hidden" data-testid="whisper-diagnostic-hallu-samples">
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-amber-800 bg-amber-100/70 border-b border-amber-200">
                        {lang === "fr"
                          ? `Segments filtrés (échantillon de ${ds.hallucination_filter_samples.length})`
                          : `Filtered segments (sample of ${ds.hallucination_filter_samples.length})`}
                      </div>
                      <div className="max-h-48 overflow-y-auto divide-y divide-amber-100">
                        {ds.hallucination_filter_samples.map((s, i) => {
                          const mmss = (sec) => {
                            const t = Math.max(0, Math.floor(Number(sec) || 0));
                            const m = Math.floor(t / 60);
                            const ss = String(t % 60).padStart(2, "0");
                            return `${m}:${ss}`;
                          };
                          return (
                            <div key={i} className="px-2 py-1 text-[11px]" data-testid={`whisper-diagnostic-hallu-sample-${i}`}>
                              <div className="flex items-center gap-2 text-zinc-500 text-[10px] tabular-nums">
                                <span className="font-mono">{mmss(s.start)}–{mmss(s.end)}</span>
                                <span className="text-zinc-400">nsp={s.no_speech_prob}</span>
                                <span className="text-zinc-400">lp={s.avg_logprob}</span>
                              </div>
                              <div className="text-zinc-800 italic truncate" title={s.text}>« {s.text} »</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </section>
              )}
              {/* Vincent B790 (Eric 2026-02-18) — Section timing & override
                  pour comprendre si un rerun-whisper a bien été exécuté. */}
              {(data.video?.whisper_rerun_at || data.video?.dedup_override) && (
                <section data-testid="whisper-diagnostic-audit">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
                    {lang === "fr" ? "Audit rerun" : "Rerun audit"}
                  </h3>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                    {data.video?.whisper_rerun_at && (
                      <>
                        <dt className="text-zinc-500">{lang === "fr" ? "Dernier rerun" : "Last rerun"}</dt>
                        <dd className="text-zinc-900 font-mono text-[10px]">
                          {new Date(data.video.whisper_rerun_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}
                        </dd>
                      </>
                    )}
                    {data.transcript?.created_at && (
                      <>
                        <dt className="text-zinc-500">{lang === "fr" ? "Transcript créé" : "Transcript created"}</dt>
                        <dd className="text-zinc-900 font-mono text-[10px]">
                          {new Date(data.transcript.created_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}
                        </dd>
                      </>
                    )}
                    {data.video?.dedup_override && (
                      <>
                        <dt className="text-zinc-500">Override en attente</dt>
                        <dd className="text-rose-700 font-mono text-[10px]" data-testid="whisper-diagnostic-pending-override">
                          {JSON.stringify(data.video.dedup_override)}
                          <span className="block text-[9px] text-rose-600 italic mt-0.5">
                            {lang === "fr"
                              ? "⚠️ Override encore posé — pipeline pas encore relancé, patiente ou vérifie les logs."
                              : "⚠️ Override still pending — pipeline hasn't rerun yet."}
                          </span>
                        </dd>
                      </>
                    )}
                  </dl>
                </section>
              )}
            </>
          ) : null}
        </div>

        <div className="p-3 border-t border-zinc-200 bg-zinc-50 flex justify-end">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-100"
            data-testid="whisper-diagnostic-footer-close-btn"
          >
            {lang === "fr" ? "Fermer" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}
